import type { LocationType } from '../../__generated__/types.gqlgenerated';

import { useThrottle } from '../../mads/util/useThrottle';
import {
    fetchLocationAutocompleteSuggestionsViaGraphql,
    type LocationSuggestion,
} from '../util/fetchLocationAutocompleteSuggestionsViaGraphql';

import { useApolloClient } from '@apollo/client';
import { useState, useEffect } from 'react';

/**
 * Custom hook for location autocomplete using GraphQL
 *
 * @param query - The search text entered by the user
 * @param enabled - Whether autocomplete is enabled
 * @param country - Country code (e.g., 'US')
 * @param language - Language code (e.g., 'en')
 * @param debounceMs - Debounce delay in milliseconds
 * @param maxResults - Maximum number of suggestions to return
 * @param locationTypeFilter - Optional filter for location types
 * @returns Object containing suggestions array and full location data
 */
export const useLocationAutocomplete = (
    query: string,
    enabled: boolean,
    country: string,
    language: string,
    debounceMs = 200,
    maxResults = 8,
    locationTypeFilter?: LocationType[],
): { suggestions: string[]; fullSuggestions: LocationSuggestion[] } => {
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [fullSuggestions, setFullSuggestions] = useState<LocationSuggestion[]>([]);

    const client = useApolloClient();
    const throttledQuery = useThrottle<string>(query, debounceMs);

    useEffect(() => {
        // Clear suggestions if query is empty, disabled, or too short
        if (!throttledQuery || !enabled || throttledQuery.length < 3) {
            setSuggestions([]);
            setFullSuggestions([]);
            return;
        }

        let cancelled = false;

        const fetchSuggestions = async () => {
            try {
                const results = await fetchLocationAutocompleteSuggestionsViaGraphql(
                    client,
                    country,
                    language,
                    throttledQuery,
                    maxResults,
                    locationTypeFilter,
                );

                // Check if the effect has been cleaned up while waiting for the results
                if (cancelled) {
                    return;
                }

                // Extract suggestion strings for the autocomplete dropdown
                const suggestionStrings = results.map((r) => r.suggestion);
                setSuggestions(suggestionStrings);
                setFullSuggestions(results);
            } catch (err) {
                // Also check here in case of a fast failure
                if (cancelled) {
                    return;
                }
                console.error('Location autocomplete error:', err);
                setSuggestions([]);
                setFullSuggestions([]);
            }
        };

        // Call the async function
        void fetchSuggestions();

        return () => {
            cancelled = true;
        };
    }, [throttledQuery, enabled, country, language, client, maxResults, locationTypeFilter]);

    return {
        suggestions,
        fullSuggestions,
    };
};
